<template>
  <div class="relative min-h-500">
    <VMessage
        type="warning"
        :fullwidth="true"
        v-if="!permissionCompany"
        data-test="permission-warning"
    >
      <template v-slot:icon>
        <ExclamationTriangleIcon />
      </template>
      <template v-slot:text>
        {{ $t('companySettings.companyProfile.permissionWarning') }}
      </template>
    </VMessage>
    <template v-if="permissionCompany">
      <div data-test="billing-details">
        <VLoadingSpinner v-if="loading" />
        <template v-else>
          <BillingOldBillingWarning v-if="!newBillingEnabled" />
          <template v-if="newBillingEnabled">
            <BillingCountryConfirmation @fetchBilling="fetchBilling" />
            <div>
              <BillingCurrentPlan
                :currentPlan="currentPlan"
                :paygPrice="paygPrice"
                :paymentMethods="paymentMethods"
                :usage="usage"
              />
              <BillingOverages
                :company="company"
                :overagesAllowed="overagesAllowed"
              />
              <BillingChangePlan
                :plansAvailable="plansAvailable"
                :currentPlan="currentPlan"
                :paymentMethods="paymentMethods"
                :paygPrice="paygPrice"
                :company="company"
                @plan-switched="handlePlanSwitch"
              />
              <BillingPaymentMethods :payment-methods="paymentMethods" />
              <BillingInvoices :invoices="invoices" />
            </div>
            <AddCreditsModal
              :show="showCreditsModal"
              @close="showCreditsModal = false;"
            />
            <AddCreditsDirectDebitBlockModal
              :show="showDDBlockModal"
              @close="showDDBlockModal = false;"
            />
            <SwitchPlanInvoicePreviewModal
              :currentPlan="currentPlan"
              :paymentMethodPending="!!paymentMethodPending"
              :paymentMethodAvailable="!!paymentMethods.length"
              :overagesEnabled="!!company.billing_overage"
              :selectedPlan="selectedPlan"
              :show="showPlanInvoicePreviewModal"
              @close="showPlanInvoicePreviewModal = false;"
              @plan-switched="handlePlanSwitch"
            />
          </template>
        </template>
      </div>
    </template>
  </div>
</template>
<script>

import { mapGetters, mapState } from 'vuex';
import AddCreditsModal from '@/views/modals/payment/AddCreditsModal.vue';
import AddCreditsDirectDebitBlockModal from '@/views/modals/payment/AddCreditsDirectDebitBlockModal.vue';
import SwitchPlanInvoicePreviewModal from '@/views/modals/payment/PlanSwitchInvoicePreviewModal.vue';

import VLoadingSpinner from '@/components/deprecated/atoms/LoadingSpinner.vue';
import BillingCurrentPlan from '@/views/components/billing/CurrentPlan.vue';
import BillingOverages from '@/views/components/billing/Overages.vue';
import BillingChangePlan from '@/views/components/billing/ChangePlan.vue';
import BillingPaymentMethods from '@/views/components/billing/PaymentMethods.vue';
import BillingInvoices from '@/views/components/billing/Invoices.vue';
import BillingOldBillingWarning from '@/views/components/billing/OldBillingWarning.vue';
import BillingCountryConfirmation from '@/views/components/billing/CountryConfirmation.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import { ExclamationTriangleIcon } from '@/components/icons/fontIcons';

export default {
  name: 'BillingView',
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (vm.redirectBackOnPlanSwitch) {
        // eslint-disable-next-line
        vm.redirectOnPlanSwitchRoute = from
      }
    });
  },
  props: {
    redirectBackOnPlanSwitch: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    BillingChangePlan,
    VLoadingSpinner,
    BillingCurrentPlan,
    BillingOverages,
    BillingPaymentMethods,
    BillingInvoices,
    AddCreditsModal,
    AddCreditsDirectDebitBlockModal,
    SwitchPlanInvoicePreviewModal,
    BillingCountryConfirmation,
    BillingOldBillingWarning,
    VMessage,
    ExclamationTriangleIcon,
  },
  data() {
    return {
      monthlyToAnnualPlanMap: {
        'small-business-monthly': 'small-business-annually',
        'medium-business-monthly': 'medium-business-annually',
        'large-business-monthly': 'large-business-annually',
        'corporate-monthly': 'corporate-annually',
        'small-business-monthly-2020': 'small-business-annually-2020',
        'medium-business-monthly-2020': 'medium-business-annually-2020',
        'large-business-monthly-2020': 'large-business-annually-2020',
        'corporate-monthly-2020': 'corporate-annually-2020',
      },
      showCreditsModal: false,
      showPlanInvoicePreviewModal: false,
      showDDBlockModal: false,
      loading: true,
      selectedPlan: {},
      newBillingEnabled: true,
      appV1Url: process.env.VUE_APP_APPV1,
      redirectOnPlanSwitchRoute: null,
    };
  },
  mounted() {
    this.$store.dispatch('company/GET').then(() => {
      if (this.company.new_billing_enabled) {
        this.fetchBilling();
      } else {
        this.newBillingEnabled = false;
        this.loading = false;
      }
    });
  },
  computed: {
    ...mapState({
      company: state => state.company.company,
    }),
    ...mapGetters({
      plansAvailable: 'plans/results',
      currentPlan: 'plans/current',
      paymentMethods: 'paymentMethods/results',
      defaultMethod: 'paymentMethods/defaultMethod',
      usage: 'metering/usage',
      invoices: 'invoices/results',
      paygPlans: 'paygPlans/results',
      userHasRole: 'roles/userHasRole',
    }),
    overagesAllowed() {
      return this.defaultMethod && this.currentPlan.id !== 'pay-as-you-go' && this.currentPlan.id !== 'free-trial';
    },
    permissionCompany() {
      return this.userHasRole('super-admin');
    },
    paymentMethodPending() {
      if (this.paymentMethods && this.paymentMethods.length) {
        const defaultMethod = this.paymentMethods.find(method => method.is_default === true);
        if (defaultMethod.status === 'pending') return true;
      }

      return false;
    },
    paygPrice() {
      if (this.paygPlans.length > 0) {
        const pricePerEnvelope = this.paygPlans[0].amount / this.paygPlans[0].quantity;
        const pricePerEnvelopeAsDecimal = parseFloat(pricePerEnvelope / 100);
        const pricePerEnvelopeWithCurrency = this.$n(pricePerEnvelopeAsDecimal, 'currency', this.paygPlans[0].currency);
        return this.$t('companySettings.billing.changePlanSection.pricePerEnvelopeFrom', { price: pricePerEnvelopeWithCurrency });
      }
      return '';
    },
  },
  methods: {
    checkDDThenAddCredits() {
      if (this.paymentMethods.length) {
        const defaultMethod = this.paymentMethods.find(method => method.is_default === true);
        if (defaultMethod.type === 'Source') {
          this.showDDBlockModal = true;
        } else {
          this.showCreditsModal = true;
        }
      } else {
        this.showCreditsModal = true;
      }
    },
    fetchBilling() {
      this.loading = true;

      const plans = this.$store.dispatch('plans/GET');
      const paygPlans = this.$store.dispatch('paygPlans/GET');
      const currentPlan = this.$store.dispatch('plans/GET_CURRENT_PLAN');
      const usage = this.$store.dispatch('metering/GET_USAGE');
      const paymentMethods = this.$store.dispatch('paymentMethods/GET');
      const invoices = this.$store.dispatch('invoices/GET');

      const allPromises = [plans, paygPlans, currentPlan, usage, paymentMethods, invoices];

      return Promise.all(allPromises).then(() => {
        this.loading = false;
        if (this.$route.query.select_plan) {
          this.openSelectPlanModalById(this.$route.query.select_plan);
        } else if (this.$route.query.add_credits_modal && this.company.currency_locked === true) {
          this.checkDDThenAddCredits();
          this.$router.push({ path: '/company-profile/billing', query: undefined });
        } else if (this.$route.query.select_annual && this.currentPlan.id in this.monthlyToAnnualPlanMap) {
          this.openSelectPlanModalById(this.monthlyToAnnualPlanMap[this.currentPlan.id]);
        }
      });
    },
    openSelectPlanModalById(id) {
      this.selectedPlan = this.plansAvailable.find(plan => plan.id === id);
      if (this.selectedPlan && (this.selectedPlan.id === 'free-trial' || this.selectedPlan.id === 'pay-as-you-go')) {
        this.$bus.$emit('toggleModal', { name: 'select-plan-modal', active: true });
      } else if (this.selectedPlan) {
        this.showPlanInvoicePreviewModal = true;
      } else {
        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.fail.find', { name: 'plan' }),
          icon: 'times',
          type: 'error',
        }));
      }
    },
    handlePlanSwitch() {
      if (this.redirectOnPlanSwitchRoute) {
        this.$router.push(this.redirectOnPlanSwitchRoute);
      }
    },
  },
};
</script>
